import { Text } from '@thanx/uikit/text'
import { alert } from 'actions/flash'
import {
  promotionApi,
  PromotionPayload,
  useDeactivatePromotionMutation,
  useExportPromotionCodesMutation,
} from 'api/promotion'
import { getErrorMessage } from 'api/utils'
import Cell from 'components/Cell'
import ModalContainer from 'components/ModalContainer'
import PopoverMenu from 'components/PopoverMenu'
import useDispatch from 'hooks/useDispatch'
import { useUrlFileDownload } from 'hooks/useUrlFileDownload'
import { buildTranslate } from 'locales'
import { Fields as MerchantFields } from 'models/Merchant'
import React, { useEffect, useState } from 'react'
import { Button } from 'react-bootstrap-five'
import { Link } from 'react-router-dom'
import { push } from 'react-router-redux'
import { errorHandler } from 'utilities/errorHandler'
import ChannelStatusCell from '../ChannelStatusCell'
import { onlineAcceptance, posAcceptance } from '../compatibility'
import '../PromoCodes.scss'
import { areCodesStillGenerating, isCsvGenerating } from '../promotionPool'

const t = buildTranslate('promo_codes.landing')

type PromotionRowProps = {
  promotion: PromotionPayload
  tab: 'active' | 'draft' | 'complete'
  // Channel-acceptance columns (LOY-622); omitted when `showChannels` is false.
  showChannels?: boolean
  merchant?: MerchantFields | null
  toastInstoreEnabled?: boolean
  onConfigureOnline?: () => void
}

const PromotionRow: React.FC<PromotionRowProps> = ({
  promotion,
  tab,
  showChannels = false,
  merchant,
  toastInstoreEnabled = false,
  onConfigureOnline,
}) => {
  const dispatch = useDispatch()
  const prefetchPromotion = promotionApi.usePrefetch('getPromotion')
  const [deactivatePromotion, { isLoading: isEnding }] =
    useDeactivatePromotionMutation()
  const [exportCodes, { isLoading: isExporting }] =
    useExportPromotionCodesMutation()
  const {
    downloadUrlFile,
    isDownloading,
    error: downloadError,
  } = useUrlFileDownload()
  const [showEndModal, setShowEndModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const pool = promotion.pool
  const staticCode = pool?.staticCode
  const isMultiCode = pool?.generationType === 'single_use'

  // Surface download failures as a toast. See Detail/index.tsx for the
  // full rationale — `useUrlFileDownload` swallows the underlying error
  // into its `error` state rather than rethrowing.
  useEffect(() => {
    if (!downloadError) return
    errorHandler.capture(downloadError, {
      promotionId: promotion.id,
      action: 'download_csv',
    })
    dispatch(alert({ key: 'danger', message: t('actions.download_error') }))
  }, [downloadError, dispatch, promotion.id])

  const codesStillGenerating = !!pool && areCodesStillGenerating(pool)
  const csvGenerating = !!pool && isCsvGenerating(pool)

  const redemptions = pool
    ? isMultiCode
      ? codesStillGenerating
        ? '—'
        : t('table.redemptions_count', {
            redeemed: pool.redeemedCount.toLocaleString(),
            total: pool.totalCodes.toLocaleString(),
          })
      : t('table.redemptions_single', {
          redeemed: pool.redeemedCount.toLocaleString(),
        })
    : '—'

  const expiration = promotion.endsAt
    ? new Date(promotion.endsAt).toLocaleDateString()
    : t('table.no_end_date')

  const handleEdit = () => {
    dispatch(push(`/promo_codes/${promotion.id}/edit`))
  }

  const handleDeactivate = async (
    successKey: string,
    errorKey: string,
    closeModal: () => void
  ) => {
    try {
      const result = await deactivatePromotion(promotion.id)
      if ('error' in result) {
        const message = getErrorMessage(result.error, t(errorKey))
        dispatch(alert({ key: 'danger', message }))
        return
      }
      dispatch(alert({ key: 'success', message: t(successKey) }))
    } catch {
      dispatch(alert({ key: 'danger', message: t(errorKey) }))
    } finally {
      closeModal()
    }
  }

  const handleExportCsv = async () => {
    const result = await exportCodes({ promotionId: promotion.id })
    if ('error' in result) {
      const message = getErrorMessage(result.error, t('actions.export_error'))
      dispatch(alert({ key: 'danger', message }))
      return
    }
    dispatch(
      alert({
        key: 'success',
        message: t('actions.export_queued'),
        timeout: 5,
      })
    )
  }

  const handleDownloadCsv = async (url: string) => {
    // Download errors land on the useEffect watching `downloadError`.
    await downloadUrlFile(url, `${promotion.name}-codes.csv`)
  }

  // Three-state CSV menu item, mirroring the Detail page:
  // URL present → Download CSV (terminal); generating → disabled spinner
  // label; else → Export CSV fallback to (re)queue generation.
  const renderCsvMenuItem = () => {
    if (!isMultiCode || !pool) return null

    const csvUrl = pool.codesCsvUrl
    if (csvUrl) {
      return (
        <Button
          className="d-block grey-90 pr-m"
          variant="link"
          onClick={() => handleDownloadCsv(csvUrl)}
          disabled={isDownloading}
          data-testid={`download-codes-${promotion.id}`}
        >
          {isDownloading ? t('actions.downloading') : t('actions.download_csv')}
        </Button>
      )
    }

    if (csvGenerating) {
      return (
        <Button
          className="d-block grey-90 pr-m"
          variant="link"
          disabled
          data-testid={`generating-csv-${promotion.id}`}
        >
          {t('actions.generating_csv')}
        </Button>
      )
    }

    return (
      <Button
        className="d-block grey-90 pr-m"
        variant="link"
        onClick={handleExportCsv}
        disabled={isExporting}
        data-testid={`export-codes-${promotion.id}`}
      >
        {isExporting ? t('actions.exporting') : t('actions.export_csv')}
      </Button>
    )
  }

  const renderCodeCell = () => {
    if (staticCode) {
      return <span className="promo-code-badge">{staticCode}</span>
    }
    if (pool?.generationType === 'single_use') {
      return (
        <Text.BodyText4 className="grey-70">
          {codesStillGenerating ? (
            <>
              <span className="fa fa-spin fa-circle-o-notch mr-xs" />
              {t('table.generating_codes')}
            </>
          ) : (
            t('table.unique_codes', {
              count: pool.totalCodes.toLocaleString(),
            })
          )}
        </Text.BodyText4>
      )
    }
    return '—'
  }

  const renderMenu = () => {
    if (tab === 'complete') return null

    return (
      <PopoverMenu
        testId={`promotion-overflow-menu-${promotion.id}`}
        ariaLabel={`Actions for ${promotion.name}`}
        dismissOnClickOutside
        closeOnClick
      >
        <Button
          className="d-block grey-90 pr-m"
          variant="link"
          onClick={handleEdit}
          data-testid={`edit-promotion-${promotion.id}`}
        >
          {t('actions.edit')}
        </Button>
        {tab === 'active' && (
          <Button
            className="d-block grey-90 pr-m"
            variant="link"
            onClick={() => setShowEndModal(true)}
            data-testid={`end-promotion-${promotion.id}`}
          >
            {t('actions.end_promotion')}
          </Button>
        )}
        {tab === 'draft' && (
          <Button
            className="d-block grey-90 pr-m"
            variant="link"
            onClick={() => setShowDeleteModal(true)}
            data-testid={`delete-draft-${promotion.id}`}
          >
            {t('actions.delete_draft')}
          </Button>
        )}
        {renderCsvMenuItem()}
      </PopoverMenu>
    )
  }

  return (
    <>
      <tr
        className="border-bottom-1 grey-30-border"
        data-testid={`promotion-row-${promotion.id}`}
      >
        <Cell>
          <Text.BodyText4 bold>{promotion.name}</Text.BodyText4>
        </Cell>
        <Cell>{renderCodeCell()}</Cell>
        <Cell>
          <Text.BodyText4>{redemptions}</Text.BodyText4>
        </Cell>
        <Cell>
          <Text.BodyText4>{expiration}</Text.BodyText4>
        </Cell>
        {showChannels && (
          <>
            <Cell>
              <ChannelStatusCell
                channel="online"
                state={onlineAcceptance(merchant, promotion.redemptionVenue)}
                onActivate={onConfigureOnline}
              />
            </Cell>
            <Cell>
              <ChannelStatusCell
                channel="pos"
                state={posAcceptance(
                  merchant,
                  toastInstoreEnabled,
                  promotion.redemptionVenue
                )}
              />
            </Cell>
          </>
        )}
        <Cell>
          <Link
            to={`/promo_codes/${promotion.id}`}
            onMouseEnter={() => prefetchPromotion(promotion.id)}
          >
            {t('table.view')}
          </Link>
        </Cell>
        <Cell>{renderMenu()}</Cell>
      </tr>

      <ModalContainer
        isOpen={showEndModal}
        onClose={() => setShowEndModal(false)}
        title={t('confirm_end.title')}
        proceedText={
          isEnding ? t('confirm_end.ending') : t('confirm_end.confirm')
        }
        onProceed={() =>
          handleDeactivate('actions.end_success', 'actions.end_error', () =>
            setShowEndModal(false)
          )
        }
        isLoading={isEnding}
      >
        <Text.BodyText3>{t('confirm_end.message')}</Text.BodyText3>
      </ModalContainer>

      <ModalContainer
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={t('confirm_delete.title')}
        proceedText={
          isEnding ? t('confirm_delete.deleting') : t('confirm_delete.confirm')
        }
        onProceed={() =>
          handleDeactivate(
            'actions.delete_success',
            'actions.delete_error',
            () => setShowDeleteModal(false)
          )
        }
        isLoading={isEnding}
      >
        <Text.BodyText3>{t('confirm_delete.message')}</Text.BodyText3>
      </ModalContainer>
    </>
  )
}

export default PromotionRow
